import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Activity, Calendar, Users, Plus, Menu, X, Trash2, Edit2, Search 
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

// Helper for authenticated API calls
const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

const WORK_TYPES = [
  { value: "assistant_nurse", label: "Assistent/Õde", color: "#64748B" },
  { value: "endocrine_nurse", label: "Endokrinoloogiaõde", color: "#8B5CF6" },
  { value: "accountant", label: "Raamatupidaja", color: "#0EA5E9" },
];

export default function NursesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [nurses, setNurses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Nurse Dialog
  const [nurseDialogOpen, setNurseDialogOpen] = useState(false);
  const [editingNurse, setEditingNurse] = useState(null);
  const [nurseForm, setNurseForm] = useState({
    name: "",
    employee_id: "",
    work_type: "assistant_nurse",
  });

  useEffect(() => {
    fetchNurses();
  }, []);

  const fetchNurses = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/nurses`);
      if (response.ok) {
        const data = await response.json();
        setNurses(data);
      }
    } catch (error) {
      console.error("Failed to fetch nurses:", error);
      toast.error("Failed to load nurses");
    } finally {
      setLoading(false);
    }
  };

  const handleNurseSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingNurse 
        ? `${API}/nurses/${editingNurse.nurse_id}`
        : `${API}/nurses`;
      
      const response = await authFetch(url, {
        method: editingNurse ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nurseForm),
      });

      if (response.ok) {
        toast.success(editingNurse ? "Nurse updated" : "Nurse created");
        setNurseDialogOpen(false);
        setEditingNurse(null);
        resetNurseForm();
        fetchNurses();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save nurse");
      }
    } catch (error) {
      console.error("Failed to save nurse:", error);
      toast.error("Failed to save nurse");
    }
  };

  const handleDeleteNurse = async (nurseId) => {
    if (!window.confirm("Are you sure you want to delete this nurse? All their schedules will also be deleted.")) return;
    
    try {
      const response = await authFetch(`${API}/nurses/${nurseId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Nurse deleted");
        fetchNurses();
      } else {
        toast.error("Failed to delete nurse");
      }
    } catch (error) {
      console.error("Failed to delete nurse:", error);
      toast.error("Failed to delete nurse");
    }
  };

  const resetNurseForm = () => {
    setNurseForm({
      name: "",
      employee_id: "",
      work_type: "assistant_nurse",
    });
  };

  const openEditDialog = (nurse) => {
    setEditingNurse(nurse);
    setNurseForm({
      name: nurse.name,
      employee_id: nurse.employee_id,
      work_type: nurse.work_type,
    });
    setNurseDialogOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getWorkType = (value) => {
    return WORK_TYPES.find((w) => w.value === value) || WORK_TYPES[0];
  };

  // Filter nurses by search
  const filteredNurses = nurses.filter((nurse) => {
    const query = searchQuery.toLowerCase();
    return (
      nurse.name.toLowerCase().includes(query) ||
      nurse.employee_id.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="nurses-page">
      <AppSidebar open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarMenuButton onClick={() => setMobileMenuOpen(true)} />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Õed</h1>
                <p className="text-sm text-slate-500">{nurses.length} registreeritud õde</p>
              </div>
            </div>
            
            <Dialog open={nurseDialogOpen} onOpenChange={(open) => {
              setNurseDialogOpen(open);
              if (!open) {
                setEditingNurse(null);
                resetNurseForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-nurse-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Lisa õde</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingNurse ? "Muuda õde" : "Lisa õde"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleNurseSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={nurseForm.name}
                      onChange={(e) => setNurseForm({ ...nurseForm, name: e.target.value })}
                      placeholder="Enter nurse name"
                      required
                      data-testid="nurse-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Employee ID</Label>
                    <Input
                      value={nurseForm.employee_id}
                      onChange={(e) => setNurseForm({ ...nurseForm, employee_id: e.target.value })}
                      placeholder="e.g., N001"
                      required
                      data-testid="nurse-id-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Primary Work Type</Label>
                    <Select
                      value={nurseForm.work_type}
                      onValueChange={(value) => setNurseForm({ ...nurseForm, work_type: value })}
                    >
                      <SelectTrigger data-testid="nurse-work-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WORK_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: type.color }}
                              />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setNurseDialogOpen(false);
                        setEditingNurse(null);
                        resetNurseForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-slate-900 hover:bg-slate-800"
                      data-testid="nurse-submit-btn"
                    >
                      {editingNurse ? "Update" : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="nurse-search-input"
            />
          </div>

          {/* Nurses Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 spinner" />
            </div>
          ) : filteredNurses.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? "No nurses found" : "No nurses yet"}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {searchQuery 
                    ? "Try adjusting your search query" 
                    : "Add your first nurse to get started"}
                </p>
                {!searchQuery && (
                  <Button 
                    onClick={() => setNurseDialogOpen(true)}
                    className="bg-slate-900 hover:bg-slate-800"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Nurse
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredNurses.map((nurse) => {
                const workType = getWorkType(nurse.work_type);
                return (
                  <Card 
                    key={nurse.nurse_id} 
                    className="border-slate-200 card-hover"
                    data-testid={`nurse-card-${nurse.nurse_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                            style={{ backgroundColor: workType.color }}
                          >
                            {nurse.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{nurse.name}</h3>
                            <p className="text-sm text-slate-500">ID: {nurse.employee_id}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditDialog(nurse)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            data-testid={`edit-nurse-${nurse.nurse_id}`}
                          >
                            <Edit2 className="w-4 h-4 text-slate-500" />
                          </button>
                          <button
                            onClick={() => handleDeleteNurse(nurse.nurse_id)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                            data-testid={`delete-nurse-${nurse.nurse_id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                      
                      <Badge 
                        variant="outline" 
                        className={`badge-${nurse.work_type}`}
                      >
                        <div 
                          className="w-2 h-2 rounded-full mr-2" 
                          style={{ backgroundColor: workType.color }}
                        />
                        {workType.label}
                      </Badge>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
